<template>
  <section class="payload-report-wrapper">
    <!-- Hero Banner / Page Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-1">
      <div>
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon icon="TruckIcon" size="24" class="mr-50" />
          {{ $t('payloadProductivityAndLoadReport') }}
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('monitorOperationalEfficiencyCyclesWeightComplianceTelemetryDeltas') }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-1 mt-md-0" style="gap: 8px;">
        <b-button
          v-if="assetId"
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          @click="showFilters = !showFilters"
          class="d-inline-flex align-items-center"
        >
          <feather-icon icon="FilterIcon" size="14" class="mr-50" />
          <span>{{ showFilters ? $t('hideFilters') : $t('filters') }}</span>
          <b-badge v-if="activeFiltersCount > 0" variant="light-danger" pill class="ml-50">
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-if="assetId"
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          @click="fetchData"
          v-b-tooltip.hover
          :title="$t('updateData')"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': isLoadingSummary || isLoadingAggregations || isLoadingDetails || loadingAsset }"
          />
        </b-button>

        <b-button
          v-if="detailsData.data && detailsData.data.length > 0"
          variant="outline-success"
          size="sm"
          @click="exportToCsv"
          class="d-inline-flex align-items-center"
          v-b-tooltip.hover
          :title="$t('exportCurrentPageDataToCsv')"
        >
          <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
          <span class="d-none d-sm-inline">{{ $t('exportCsv') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Compact Asset Info Header Card (Inspired by weeklyWorkStatus.vue) -->
    <b-card
      v-if="assetId"
      no-body
      class="asset-header-card mb-2 shadow-sm"
    >
      <div class="asset-header-content p-1 p-md-1">
        <!-- Skeleton Loading State -->
        <div
          v-if="loadingAsset"
          class="d-flex align-items-center justify-content-between flex-wrap w-100 py-50"
        >
          <div class="d-flex align-items-center">
            <b-spinner
              variant="primary"
              small
              class="mr-1"
            />
            <span class="text-muted font-small-3">{{ $t('loadingAssetInformation') }}</span>
          </div>
        </div>

        <!-- Loaded Content -->
        <div
          v-else
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100"
        >
          <!-- Left: Back Button, Avatar, Plate, Marca, Modelo -->
          <div class="d-flex align-items-center mb-1 mb-md-0 mr-md-2">
            <!-- Back Button -->
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon rounded-circle mr-1 p-50 flex-shrink-0 back-btn"
              :title="$t('back')"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
            </b-button>

            <!-- Vehicle Avatar -->
            <div class="asset-avatar-box mr-1 flex-shrink-0">
              <img
                :src="assetAvatar"
                :alt="assetModel || 'Asset'"
                class="asset-avatar-img"
                @error="handleImageError"
              >
            </div>

            <!-- Vehicle Identifiers: Plate + Marca / Modelo -->
            <div class="asset-title-group">
              <div class="d-flex align-items-center flex-wrap">
                <!-- Plate Badge -->
                <div
                  v-if="assetPlate"
                  v-b-tooltip.hover
                  class="license-plate-badge mr-75 cursor-pointer"
                  :title="$t('clickToCopyLicensePlate')"
                  @click="copyToClipboard(assetPlate, $t('licensePlate'))"
                >
                  <span class="plate-number">{{ assetPlate }}</span>
                  <feather-icon
                    icon="CopyIcon"
                    size="11"
                    class="ml-25 text-muted copy-icon"
                  />
                </div>

                <!-- Marca & Modelo -->
                <div class="d-inline-flex align-items-baseline flex-wrap">
                  <span
                    v-if="assetManufacturer"
                    class="asset-brand-text font-weight-bold mr-50"
                  >
                    {{ assetManufacturer }}
                  </span>
                  <h5 class="mb-0 font-weight-bolder text-dark asset-model-title">
                    {{ assetModel || assetPlate || assetChassis || assetId }}
                  </h5>
                </div>

                <!-- Nickname Tag if any -->
                <b-badge
                  v-if="assetData && assetData.nickname"
                  variant="light-primary"
                  class="ml-50 badge-glow font-weight-normal"
                >
                  {{ assetData.nickname }}
                </b-badge>
              </div>

              <!-- Subtitle with Chassis if no plate -->
              <div
                v-if="!assetPlate && assetChassis"
                class="d-flex align-items-center mt-25"
              >
                <small class="text-muted font-weight-bold">
                  {{ $t('chassis') }}:
                  <span
                    v-b-tooltip.hover
                    class="font-family-monospace text-primary cursor-pointer"
                    :title="$t('clickToCopyChassis')"
                    @click="copyToClipboard(assetChassis, $t('chassis'))"
                  >
                    {{ assetChassis }}
                    <feather-icon
                      icon="CopyIcon"
                      size="11"
                      class="ml-25 text-muted"
                    />
                  </span>
                </small>
              </div>
            </div>
          </div>

          <!-- Right: Chassis Chip, Group, Customer & Action Button -->
          <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
            <!-- Chassis Chip (only shown when plate is already shown on the left) -->
            <div
              v-if="assetChassis && assetPlate"
              v-b-tooltip.hover
              class="meta-chip cursor-pointer mr-50 mb-50 mb-md-0"
              :title="$t('clickToCopyChassis')"
              @click="copyToClipboard(assetChassis, $t('chassis'))"
            >
              <feather-icon
                icon="HashIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('chassis') }}:</span>
              <span class="meta-value font-family-monospace">{{ assetChassis }}</span>
              <feather-icon
                icon="CopyIcon"
                size="12"
                class="ml-50 text-muted copy-icon"
              />
            </div>

            <!-- Group Chip if any -->
            <div
              v-if="assetGroupName"
              class="meta-chip mr-50 mb-50 mb-md-0"
            >
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('group') }}:</span>
              <span class="meta-value">{{ assetGroupName }}</span>
            </div>

            <!-- Customer Chip if any -->
            <div
              v-if="assetCustomerName"
              class="meta-chip mr-50 mb-50 mb-md-0"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('customer') }}:</span>
              <span class="meta-value">{{ assetCustomerName }}</span>
            </div>

            <!-- Action Button to Asset Summary -->
            <div class="asset-actions flex-shrink-0">
              <b-button
                v-b-tooltip.hover
                variant="outline-primary"
                size="sm"
                class="btn-view-details d-inline-flex align-items-center"
                @click="goToAssetSummary"
                :title="$t('goToAssetSummary')"
              >
                <feather-icon
                  icon="ActivityIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('assetSummary') }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Informative Tip Banner -->
    <b-alert v-model="showTipBanner" variant="light-primary" dismissible class="mb-1 border-primary shadow-xs">
      <div class="d-flex align-items-center py-25">
        <b-avatar variant="light-primary" size="42px" class="mr-1 shadow-xs flex-shrink-0">
          <feather-icon icon="InfoIcon" size="22" class="text-primary" />
        </b-avatar>
        <div>
          <h6 class="mb-25 font-weight-bolder text-primary">
            {{ $t('understandPayloadTelemetry') }}
          </h6>
          <p class="mb-0 font-small-3 text-muted">
            {{ $t('thisReportCalculatesAssetProductivity') }}
            <strong>{{ $t('assetNominalLoadT') }}</strong>,
            {{ $t('systemAnalyzesAndClassifiesCycles') }}
          </p>
        </div>
      </div>
    </b-alert>

    <!-- Advanced Filter Card -->
    <transition name="slide-fade">
      <b-card v-if="showFilters && assetId" no-body class="mb-1 shadow-sm border-0 filters-card">
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon icon="SlidersIcon" size="16" class="text-primary mr-50" />
            <h6 class="mb-0 font-weight-bolder">{{ $t('reportFilters') }}</h6>
            <b-badge v-if="activeFiltersCount > 0" variant="light-primary" pill class="ml-50">
              {{ activeFiltersCount }} {{ $t('applied') }}
            </b-badge>
          </div>

          <div class="d-flex align-items-center" style="gap: 6px;">
            <b-button
              v-if="activeFiltersCount > 0"
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              @click="clearFilters"
              v-b-tooltip.hover
              :title="$t('resetFilters')"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="showFilters = false"
              v-b-tooltip.hover
              :title="$t('close')"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <!-- Quick Date Range Chips -->
          <div class="mb-1 d-flex flex-wrap align-items-center" style="gap: 6px;">
            <small class="font-weight-bold text-muted mr-50">{{ $t('periodShortcuts') }}:</small>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'today' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('today')"
            >
              {{ $t('today') }}
            </b-badge>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'yesterday' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('yesterday')"
            >
              {{ $t('yesterday') }}
            </b-badge>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'last7' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('last7')"
            >
              {{ $t('last7Days') }}
            </b-badge>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'month' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('month')"
            >
              {{ $t('thisMonth') }}
            </b-badge>
          </div>

          <b-row>
            <!-- Date Start -->
            <b-col cols="12" md="4" class="mb-1">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="CalendarIcon" size="13" class="mr-25 text-primary" />
                <span>{{ $t('startDate') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon icon="CalendarIcon" size="14" />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateStart"
                  class="form-control"
                  :config="flatPickrConfig"
                  :placeholder="$t('select')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <!-- Date End -->
            <b-col cols="12" md="4" class="mb-1">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="CalendarIcon" size="13" class="mr-25 text-primary" />
                <span>{{ $t('endDate') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon icon="CalendarIcon" size="14" />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateEnd"
                  class="form-control"
                  :config="flatPickrConfig"
                  :placeholder="$t('select')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <!-- Target Load (Carga Nominal) -->
            <b-col cols="12" md="4" class="mb-1">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="TargetIcon" size="13" class="mr-25 text-primary" />
                <span>{{ $t('nominalLoadT') }}</span>
                <feather-icon
                  icon="HelpCircleIcon"
                  size="12"
                  class="ml-25 text-muted cursor-pointer"
                  v-b-tooltip.hover
                  :title="$t('assetLoadCorrespondsToNominalLoadCapacity')"
                />
              </label>
              <b-input-group size="sm">
                <b-form-input
                  ref="targetLoadInput"
                  v-model="targetLoad"
                  type="number"
                  step="0.1"
                  min="0.1"
                  :placeholder="$t('exNominal')"
                />
                <b-input-group-append is-text>
                  <small class="font-weight-bold">t</small>
                </b-input-group-append>
              </b-input-group>
            </b-col>
          </b-row>

          <!-- Filter Action Bar -->
          <div class="d-flex align-items-center justify-content-between flex-wrap mt-1 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center">
              <feather-icon icon="LockIcon" size="14" class="mr-50 text-primary" />
              <span>
                {{ $t('assetFixedByUrlId') }}
                <strong class="text-primary">{{ assetPlate || assetChassis || assetId }}</strong>.
                {{ $t('setPeriodAndNominalLoadToRefineAnalysis') }}
              </span>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <b-button
                v-if="activeFiltersCount > 0"
                variant="outline-secondary"
                size="sm"
                @click="clearFilters"
              >
                {{ $t('resetFilters') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                @click="onSearchClick"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="d-inline-flex align-items-center"
              >
                <feather-icon icon="SearchIcon" size="14" class="mr-50" />
                <span>{{ $t('searchData') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Active Filters Tag Bar (Visible when filters card is hidden) -->
    <div
      v-if="assetId && !showFilters"
      class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
      style="gap: 6px;"
    >
      <small class="font-weight-bold text-muted mr-50">{{ $t('activeFilters') }}:</small>
      <b-badge variant="light-primary" pill class="d-inline-flex align-items-center">
        <feather-icon icon="LockIcon" size="12" class="mr-25" />
        {{ assetPlate || assetChassis || assetId }}
      </b-badge>
      <b-badge v-if="dateStart || dateEnd" variant="light-primary" pill class="d-inline-flex align-items-center">
        <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
        {{ formatDateDisplay(dateStart) }} — {{ formatDateDisplay(dateEnd) }}
      </b-badge>
      <b-badge v-if="targetLoad" variant="light-warning" pill class="d-inline-flex align-items-center">
        <feather-icon icon="TargetIcon" size="12" class="mr-25" />
        {{ $t('nominalLoad') }}: {{ targetLoad }} t
        <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="clearTargetLoad" />
      </b-badge>
      <b-link class="font-small-2 font-weight-bold ml-50 text-danger" @click="clearFilters">
        {{ $t('resetFiltersLower') }}
      </b-link>
    </div>

    <!-- Empty State Prompt: No asset provided in URL -->
    <b-card v-if="!assetId" class="shadow-sm border-0 text-center py-4 mb-2">
      <b-avatar variant="light-warning" size="70px" class="mb-1 shadow-xs">
        <feather-icon icon="AlertCircleIcon" size="36" class="text-warning" />
      </b-avatar>
      <h4 class="font-weight-bolder mb-50">{{ $t('noAssetSpecifiedInRoute') }}</h4>
      <p class="text-muted font-small-3 mb-1 mx-auto" style="max-width: 520px;">
        {{ $t('thisReportAnalyzesPayloadAndOperationalTelemetry') }}
      </p>
      <b-button
        variant="primary"
        size="sm"
        :to="{ name: 'apps-asset-list' }"
        class="d-inline-flex align-items-center"
      >
        <feather-icon icon="TruckIcon" size="14" class="mr-50" />
        <span>{{ $t('goToAssetList') }}</span>
      </b-button>
    </b-card>

    <template v-else>
      <!-- SECTION 1: KPI CARDS -->
      <b-row class="match-height mb-1">
        <!-- Card 1: Carga Total Acumulada -->
        <b-col cols="12" sm="6" lg="3" class="mb-1 mb-lg-0">
          <b-card no-body class="kpi-card shadow-sm border-0 h-100 p-1">
            <template v-if="isLoadingSummary">
              <div class="d-flex align-items-center mb-1">
                <b-skeleton type="avatar" size="48px" class="mr-1" />
                <div class="flex-grow-1">
                  <b-skeleton width="70%" height="16px" class="mb-25" />
                  <b-skeleton width="45%" height="12px" />
                </div>
              </div>
              <b-skeleton width="85%" height="28px" class="mb-25" />
              <b-skeleton width="50%" height="14px" />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-50">
                <div>
                  <span class="kpi-label text-muted font-small-2 font-weight-bolder text-uppercase">
                    {{ $t('totalLoadTransported') }}
                  </span>
                  <h3 class="font-weight-bolder text-primary mb-0 mt-25">
                    {{ formatNumber(summaryKpis.totalLoad) }} <small class="kpi-unit">t</small>
                  </h3>
                </div>
                <b-avatar variant="light-primary" size="46px" class="shadow-xs flex-shrink-0">
                  <feather-icon icon="PackageIcon" size="22" class="text-primary" />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-2 text-muted mt-auto pt-50 border-top d-flex align-items-center">
                <feather-icon icon="LayersIcon" size="12" class="mr-25 text-primary" />
                <span>{{ $t('accumulatedNetVolumeInSelectedPeriod') }}</span>
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- Card 2: Total de Ciclos Realizados -->
        <b-col cols="12" sm="6" lg="3" class="mb-1 mb-lg-0">
          <b-card no-body class="kpi-card shadow-sm border-0 h-100 p-1">
            <template v-if="isLoadingSummary">
              <div class="d-flex align-items-center mb-1">
                <b-skeleton type="avatar" size="48px" class="mr-1" />
                <div class="flex-grow-1">
                  <b-skeleton width="70%" height="16px" class="mb-25" />
                  <b-skeleton width="45%" height="12px" />
                </div>
              </div>
              <b-skeleton width="85%" height="28px" class="mb-25" />
              <b-skeleton width="50%" height="14px" />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-50">
                <div>
                  <span class="kpi-label text-muted font-small-2 font-weight-bolder text-uppercase">
                    {{ $t('cyclesCompleted') }}
                  </span>
                  <h3 class="font-weight-bolder text-info mb-0 mt-25">
                    {{ formatInteger(summaryKpis.totalCycles) }}
                  </h3>
                </div>
                <b-avatar variant="light-info" size="46px" class="shadow-xs flex-shrink-0">
                  <feather-icon icon="RepeatIcon" size="22" class="text-info" />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-2 text-muted mt-auto pt-50 border-top d-flex align-items-center">
                <feather-icon icon="CheckCircleIcon" size="12" class="mr-25 text-info" />
                <span>{{ $t('totalTripsCyclesCompletedInSelectedPeriod') }}</span>
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- Card 3: Carga Média por Ciclo -->
        <b-col cols="12" sm="6" lg="3" class="mb-1 mb-sm-0">
          <b-card no-body class="kpi-card shadow-sm border-0 h-100 p-1">
            <template v-if="isLoadingSummary">
              <div class="d-flex align-items-center mb-1">
                <b-skeleton type="avatar" size="48px" class="mr-1" />
                <div class="flex-grow-1">
                  <b-skeleton width="70%" height="16px" class="mb-25" />
                  <b-skeleton width="45%" height="12px" />
                </div>
              </div>
              <b-skeleton width="85%" height="28px" class="mb-25" />
              <b-skeleton width="50%" height="14px" />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-50">
                <div>
                  <span class="kpi-label text-muted font-small-2 font-weight-bolder text-uppercase">
                    {{ $t('averageLoadPerCycle') }}
                  </span>
                  <h3 class="font-weight-bolder text-success mb-0 mt-25">
                    {{ formatNumber(summaryKpis.avgLoadPerCycle) }} <small class="kpi-unit">t/ciclo</small>
                  </h3>
                </div>
                <b-avatar variant="light-success" size="46px" class="shadow-xs flex-shrink-0">
                  <feather-icon icon="TrendingUpIcon" size="22" class="text-success" />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-2 text-muted mt-auto pt-50 border-top d-flex align-items-center">
                <feather-icon icon="ActivityIcon" size="12" class="mr-25 text-success" />
                <span>{{ $t('ratioTotalLoadTotalCycles') }}</span>
              </div>
            </template>
          </b-card>
        </b-col>

        <!-- Card 4: Taxa de Conformidade -->
        <b-col cols="12" sm="6" lg="3">
          <b-card no-body class="kpi-card shadow-sm border-0 h-100 p-1">
            <template v-if="isLoadingSummary">
              <div class="d-flex align-items-center mb-1">
                <b-skeleton type="avatar" size="48px" class="mr-1" />
                <div class="flex-grow-1">
                  <b-skeleton width="70%" height="16px" class="mb-25" />
                  <b-skeleton width="45%" height="12px" />
                </div>
              </div>
              <b-skeleton width="85%" height="28px" class="mb-25" />
              <b-skeleton width="50%" height="14px" />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start mb-50">
                <div>
                  <div class="d-flex align-items-center">
                    <span class="kpi-label text-muted font-small-2 font-weight-bolder text-uppercase">
                      {{ $t('complianceRate') }}
                    </span>
                    <feather-icon
                      icon="HelpCircleIcon"
                      size="12"
                      class="ml-25 text-muted cursor-pointer"
                      v-b-tooltip.hover
                      :title="$t('measuresPercentageOfTripsCompletedWithinIdealWeightRange')"
                    />
                  </div>
                  <h3
                    v-if="summaryKpis.complianceRate !== null && summaryKpis.complianceRate !== undefined"
                    class="font-weight-bolder mb-0 mt-25"
                    :class="getComplianceVariantClass(summaryKpis.complianceRate)"
                  >
                    {{ Number(summaryKpis.complianceRate).toFixed(1) }}%
                  </h3>
                  <div v-else class="mt-25">
                    <b-badge variant="light-secondary" class="font-small-2">
                      {{ $t('noNominalLoad') }}
                    </b-badge>
                  </div>
                </div>
                <b-avatar
                  :variant="summaryKpis.complianceRate !== null ? 'light-warning' : 'light-secondary'"
                  size="46px"
                  class="shadow-xs flex-shrink-0"
                >
                  <feather-icon icon="AwardIcon" size="22" :class="summaryKpis.complianceRate !== null ? 'text-warning' : 'text-muted'" />
                </b-avatar>
              </div>
              <div class="kpi-footer font-small-2 text-muted mt-auto pt-50 border-top d-flex align-items-center justify-content-between">
                <span
                  v-if="summaryKpis.complianceRate !== null"
                  v-b-tooltip.hover
                  class="cursor-pointer"
                  :title="$t('percentageOfCyclesCompletedBetween90And110')"
                >
                  {{ $t('cyclesInIdealRange') }}
                </span>
                <b-link
                  v-else
                  class="text-primary font-weight-bold"
                  @click="openFilterForTargetLoad"
                >
                  {{ $t('setNominalLoad') }}
                </b-link>
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>

      <!-- SECTION 2: CYCLE COMPLIANCE DISTRIBUTION (DONUT / PIZZA) -->
      <b-card v-if="hasTargetLoad && summaryDistribution" no-body class="shadow-sm border-0 mb-1 p-1">
        <div class="d-flex align-items-center justify-content-between mb-1 pb-50 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon icon="PieChartIcon" size="18" class="text-primary mr-50" />
            <div>
              <h6 class="mb-0 font-weight-bolder">{{ $t('cycleComplianceDistribution') }}</h6>
              <small class="text-muted">{{ $t('cycleClassificationBasedOnNominalLoad') }} {{ targetLoad }} t</small>
            </div>
          </div>
          <b-badge variant="light-primary" pill>
            {{ $t('nominalLoad') }}: {{ targetLoad }} t
          </b-badge>
        </div>

        <template v-if="isLoadingSummary">
          <div class="text-center py-3">
            <b-spinner variant="primary" class="mb-1" />
            <b-skeleton width="60%" height="20px" class="mx-auto" />
          </div>
        </template>
        <template v-else>
          <b-row class="align-items-center">
            <!-- Progress and Stats Cards -->
            <b-col cols="12" md="5" lg="4">
              <div class="distribution-pill-box p-75 mb-75 rounded border border-warning bg-light-warning">
                <div class="d-flex justify-content-between align-items-center mb-25">
                  <span class="font-small-2 font-weight-bolder text-warning">
                    <feather-icon icon="ArrowDownIcon" size="12" class="mr-25" />
                    {{ $t('underload') }} (&lt; 90%)
                  </span>
                  <strong class="font-small-2 text-warning">
                    {{ summaryDistribution.underload ? summaryDistribution.underload.percentage : 0 }}%
                  </strong>
                </div>
                <div class="d-flex align-items-baseline justify-content-between">
                  <h5 class="mb-0 font-weight-bolder text-warning">
                    {{ summaryDistribution.underload ? summaryDistribution.underload.cycles : 0 }}
                    <small class="font-small-1">{{ $t('cycles') }}</small>
                  </h5>
                  <small class="text-muted font-small-1">
                    &lt; {{ formatNumber(Number(targetLoad) * 0.9) }} t
                  </small>
                </div>
              </div>

              <div class="distribution-pill-box p-75 mb-75 rounded border border-success bg-light-success">
                <div class="d-flex justify-content-between align-items-center mb-25">
                  <span class="font-small-2 font-weight-bolder text-success">
                    <feather-icon icon="CheckIcon" size="12" class="mr-25" />
                    {{ $t('optimal') }} (90% - 110%)
                  </span>
                  <strong class="font-small-2 text-success">
                    {{ summaryDistribution.optimal ? summaryDistribution.optimal.percentage : 0 }}%
                  </strong>
                </div>
                <div class="d-flex align-items-baseline justify-content-between">
                  <h5 class="mb-0 font-weight-bolder text-success">
                    {{ summaryDistribution.optimal ? summaryDistribution.optimal.cycles : 0 }}
                    <small class="font-small-1">{{ $t('cycles') }}</small>
                  </h5>
                  <small class="text-muted font-small-1">
                    {{ formatNumber(Number(targetLoad) * 0.9) }} - {{ formatNumber(Number(targetLoad) * 1.1) }} t
                  </small>
                </div>
              </div>

              <div class="distribution-pill-box p-75 mb-75 rounded border border-danger bg-light-danger">
                <div class="d-flex justify-content-between align-items-center mb-25">
                  <span class="font-small-2 font-weight-bolder text-danger">
                    <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
                    {{ $t('overload') }} (110% - 120%)
                  </span>
                  <strong class="font-small-2 text-danger">
                    {{ summaryDistribution.overload ? summaryDistribution.overload.percentage : 0 }}%
                  </strong>
                </div>
                <div class="d-flex align-items-baseline justify-content-between">
                  <h5 class="mb-0 font-weight-bolder text-danger">
                    {{ summaryDistribution.overload ? summaryDistribution.overload.cycles : 0 }}
                    <small class="font-small-1">{{ $t('cycles') }}</small>
                  </h5>
                  <small class="text-muted font-small-1">
                    {{ formatNumber(Number(targetLoad) * 1.1) }} - {{ formatNumber(Number(targetLoad) * 1.2) }} t
                  </small>
                </div>
              </div>

              <div class="distribution-pill-box severe-overload p-75 rounded border">
                <div class="d-flex justify-content-between align-items-center mb-25">
                  <span class="font-small-2 font-weight-bolder text-severe">
                    <feather-icon icon="AlertOctagonIcon" size="12" class="mr-25" />
                    {{ $t('severeOverload') }} (&gt; 120%)
                  </span>
                  <strong class="font-small-2 text-severe">
                    {{ summaryDistribution.severeOverload ? summaryDistribution.severeOverload.percentage : 0 }}%
                  </strong>
                </div>
                <div class="d-flex align-items-baseline justify-content-between">
                  <h5 class="mb-0 font-weight-bolder text-severe">
                    {{ summaryDistribution.severeOverload ? summaryDistribution.severeOverload.cycles : 0 }}
                    <small class="font-small-1">{{ $t('cycles') }}</small>
                  </h5>
                  <small class="text-muted font-small-1">
                    &gt; {{ formatNumber(Number(targetLoad) * 1.2) }} t
                  </small>
                </div>
              </div>

              <!-- Target Load Classification Criteria Box -->
              <div class="mt-75 p-75 rounded bg-light-secondary border font-small-2 shadow-xs">
                <div class="font-weight-bolder text-dark mb-50 d-flex align-items-center">
                  <feather-icon icon="TargetIcon" size="13" class="mr-25 text-primary" />
                  <span>{{ $t('nominalLoadClassificationCriteria') }}</span>
                </div>
                <div class="d-flex flex-column font-small-1" style="gap: 4px;">
                  <div class="d-flex align-items-center justify-content-between">
                    <span class="text-warning font-weight-bold">● {{ $t('underloadCriteria') }}</span>
                    <b-badge variant="light-warning" pill class="font-small-1 font-weight-bolder">&lt; 90% {{ $t('ofNominalLoad') }}</b-badge>
                  </div>
                  <div class="d-flex align-items-center justify-content-between">
                    <span class="text-success font-weight-bold">● {{ $t('optimalCriteria') }}</span>
                    <b-badge variant="light-success" pill class="font-small-1 font-weight-bolder">90% a 110% {{ $t('ofNominalLoad') }}</b-badge>
                  </div>
                  <div class="d-flex align-items-center justify-content-between">
                    <span class="text-danger font-weight-bold">● {{ $t('overloadCriteria') }}</span>
                    <b-badge variant="light-danger" pill class="font-small-1 font-weight-bolder">110% a 120% {{ $t('ofNominalLoad') }}</b-badge>
                  </div>
                  <div class="d-flex align-items-center justify-content-between">
                    <span class="text-severe font-weight-bold">● {{ $t('severeOverloadCriteria') }}</span>
                    <b-badge class="badge-severe-overload font-small-1 font-weight-bolder" pill>&gt; 120% {{ $t('ofNominalLoad') }}</b-badge>
                  </div>
                </div>
                <div class="mt-50 pt-25 border-top text-muted font-small-1">
                  * {{ $t('configuredLoadIsNominalCapacity') }}
                </div>
              </div>
            </b-col>

            <!-- Donut Chart - Increased Size -->
            <b-col cols="12" md="7" lg="8" class="mt-1 mt-md-0 d-flex justify-content-center">
              <div style="width: 100%; max-width: 540px;">
                <vue-apex-charts
                  type="donut"
                  height="340"
                  :options="donutChartOptions"
                  :series="donutChartSeries"
                />
              </div>
            </b-col>
          </b-row>
        </template>
      </b-card>

      <!-- If no targetLoad: Motivational banner to set it -->
      <b-card v-else class="shadow-sm border-0 mb-1 target-load-promo-card">
        <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between py-50">
          <div class="d-flex align-items-center mb-1 mb-md-0">
            <b-avatar variant="light-warning" size="48px" class="mr-1 shadow-xs flex-shrink-0">
              <feather-icon icon="TargetIcon" size="24" class="text-warning" />
            </b-avatar>
            <div>
              <h6 class="mb-25 font-weight-bolder text-warning">
                {{ $t('unlockComplianceAnalysisAndHistogram') }}
              </h6>
              <p class="mb-0 font-small-3 text-muted" style="max-width: 680px;">
                {{ $t('assetLoadCorrespondsToNominalLoadCapacitySystemCalculates') }}
              </p>
            </div>
          </div>
          <b-button
            variant="warning"
            size="sm"
            @click="openFilterForTargetLoad"
            class="d-inline-flex align-items-center flex-shrink-0"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            <span>{{ $t('informNominalLoadT') }}</span>
          </b-button>
        </div>
      </b-card>

      <!-- SECTION 3: AGGREGATED TELEMETRY (LINE CHART & BAR CHART) -->
      <b-card no-body class="shadow-sm border-0 mb-1 p-1">
        <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-1 pb-50 border-bottom" style="gap: 10px;">
          <div class="d-flex align-items-center">
            <feather-icon icon="ActivityIcon" size="18" class="text-primary mr-50" />
            <div>
              <h6 class="mb-0 font-weight-bolder">{{ $t('aggregatedTelemetryTemporalEvolutionAndHistogram') }}</h6>
              <small class="text-muted">{{ $t('chronologicalSeriesAndCycleDistribution') }}</small>
            </div>
          </div>

          <!-- Controls: Group By Switch & Delta Help Button -->
          <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
            <b-button
              id="btn-deltas-info"
              variant="outline-primary"
              size="sm"
              class="d-inline-flex align-items-center"
            >
              <feather-icon icon="HelpCircleIcon" size="13" class="mr-25" />
              <span>{{ $t('howToInterpretTheData') }}</span>
            </b-button>
            <b-popover
              target="btn-deltas-info"
              triggers="hover focus"
              placement="bottom"
              custom-class="deltas-popover"
            >
              <template #title>
                <div class="d-flex align-items-center font-weight-bolder">
                  <feather-icon icon="HelpCircleIcon" size="14" class="mr-50 text-primary" />
                  <span>{{ $t('understandDeltasAndChartReading') }}</span>
                </div>
              </template>
              <div class="font-small-2">
                <div class="mb-75">
                  <div class="font-weight-bolder text-primary mb-25 border-bottom pb-25">
                    {{ $t('whatEachMetricMeans') }}
                  </div>
                  <p class="mb-50">
                    <strong class="text-primary">● {{ $t('deltaLoadT') }}</strong>
                    {{ $t('netLoadVolumeTransportedExclusivelyDuringInterval') }}
                  </p>
                  <p class="mb-50">
                    <strong class="text-info">● {{ $t('deltaCycles') }}</strong>
                    {{ $t('amountOfTripsOrCyclesCompletedInTimeInterval') }}
                  </p>
                  <p class="mb-0">
                    <strong class="text-success">● {{ $t('averagePerCycleT') }}</strong>
                    {{ $t('averageTransportProductivityPerTripInInterval') }}
                  </p>
                </div>

                <div>
                  <div class="font-weight-bolder text-primary mb-25 border-bottom pb-25">
                    {{ $t('howToInterpretTemporalEvolution') }}
                  </div>
                  <p class="mb-50">
                    <strong>📈 {{ $t('peaksAndValleys') }}</strong>
                    {{ $t('peaksIndicateHighIntensityTransport') }}
                  </p>
                  <p class="mb-50">
                    <strong>⚖️ {{ $t('efficiencyPerTrip') }}</strong>
                    {{ $t('compareAveragePerCycleWithNominalLoad') }}
                  </p>
                  <p class="mb-0">
                    <strong>📊 {{ $t('relationshipWithHistogram') }}</strong>
                    {{ $t('timeSeriesShowsWhenLoadWasMoved') }}
                  </p>
                </div>
              </div>
            </b-popover>

            <!-- Group By Switch (Hour vs Day) -->
            <div class="d-flex align-items-center" style="gap: 6px;">
              <small class="font-weight-bold text-muted">{{ $t('grouping') }}:</small>
              <b-button-group size="sm">
                <b-button
                  :variant="groupBy === 'Hour' ? 'primary' : 'outline-primary'"
                  @click="changeGroupBy('Hour')"
                  class="d-inline-flex align-items-center"
                >
                  <feather-icon icon="ClockIcon" size="12" class="mr-25" />
                  <span>{{ $t('byHour') }}</span>
                </b-button>
                <b-button
                  :variant="groupBy === 'Day' ? 'primary' : 'outline-primary'"
                  @click="changeGroupBy('Day')"
                  class="d-inline-flex align-items-center"
                >
                  <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                  <span>{{ $t('byDay') }}</span>
                </b-button>
              </b-button-group>
            </div>
          </div>
        </div>

        <template v-if="isLoadingAggregations">
          <div class="py-2">
            <b-skeleton width="40%" height="20px" class="mb-1" />
            <b-skeleton height="260px" animation="wave" />
            <b-skeleton width="30%" height="20px" class="mt-2 mb-1" />
            <b-skeleton height="180px" animation="wave" />
          </div>
        </template>
        <template v-else-if="timeSeriesData && timeSeriesData.length > 0">
          <!-- SUB-SECTION 3A: LINE CHART (TIME SERIES) -->
          <div class="time-series-section mb-2">
            <div class="d-flex align-items-center flex-wrap mb-1" style="gap: 8px;">
              <!-- Horizontal scroll indication if many points -->
              <div v-if="timeSeriesData.length > 16">
                <b-badge variant="light-primary" pill class="d-inline-flex align-items-center py-25 px-50 font-small-1">
                  <feather-icon icon="MoveIcon" size="12" class="mr-25" />
                  {{ $t('swipeHorizontallyToNavigate') }} {{ timeSeriesData.length }} {{ $t('periodPoints') }}
                </b-badge>
              </div>
            </div>

            <!-- Scrollable Container for Line Chart -->
            <div class="time-series-scroll-container">
              <div :style="{ minWidth: timeSeriesChartMinWidth }">
                <vue-apex-charts
                  :key="groupBy"
                  type="area"
                  height="350"
                  :options="timeSeriesChartOptions"
                  :series="timeSeriesChartSeries"
                />
              </div>
            </div>
          </div>

          <!-- SUB-SECTION 3B: HISTOGRAM (BAR CHART) -->
          <div class="histogram-section pt-1 border-top">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <div>
                <h6 class="mb-25 font-weight-bolder d-flex align-items-center text-primary">
                  <feather-icon icon="BarChart2Icon" size="16" class="mr-50" />
                  {{ $t('histogramOfCyclesByLoadRanges') }}
                </h6>
                <small class="text-muted">
                  {{ $t('cycleDistributionInWeightRangesCorrespondingToAggregatedQuery') }}
                </small>
              </div>
            </div>

            <template v-if="histogramData && histogramData.length > 0">
              <vue-apex-charts
                type="bar"
                height="250"
                :options="histogramChartOptions"
                :series="histogramChartSeries"
              />
            </template>
            <template v-else>
              <div class="text-center py-3 text-muted">
                <feather-icon icon="BarChartIcon" size="32" class="mb-50 text-muted" />
                <p class="mb-0 font-small-3">{{ $t('noHistogramDataForSelectedPeriodAndAsset') }}</p>
              </div>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="text-center py-4 text-muted">
            <b-avatar variant="light-secondary" size="50px" class="mb-1">
              <feather-icon icon="BarChart2Icon" size="24" class="text-muted" />
            </b-avatar>
            <h6 class="font-weight-bold mb-25">{{ $t('noTelemetryFoundInPeriod') }}</h6>
            <p class="font-small-3 mb-0">{{ $t('adjustPeriodOrSelectAnotherAsset') }}</p>
          </div>
        </template>
      </b-card>

      <!-- SECTION 4: DETAILS TABLE (PAGINATED SAMPLES) -->
      <b-card no-body class="asset-list-card shadow-sm border-0 mb-2">
        <!-- Table Header Toolbar -->
        <div class="p-1 border-bottom d-flex flex-column flex-md-row align-items-md-center justify-content-between" style="gap: 12px;">
          <div class="d-flex align-items-center">
            <feather-icon icon="DatabaseIcon" size="18" class="text-primary mr-50" />
            <div>
              <h6 class="mb-0 font-weight-bolder d-inline mr-50">{{ $t('payloadSamplesAndRecords') }}</h6>
              <b-badge variant="light-primary" pill>
                {{ detailsData.totalItems }} {{ $t('registeredSamples') }}
              </b-badge>
            </div>
          </div>

          <div class="d-flex align-items-center flex-wrap" style="gap: 12px;">
            <!-- Per Page Selector -->
            <div class="d-flex align-items-center">
              <label class="mr-50 mb-0 font-small-3 text-muted">{{ $t('show') }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mr-50"
                @input="onPerPageChange"
              />
              <label class="mb-0 font-small-3 text-muted">{{ $t('perPage') }}</label>
            </div>

            <!-- Refresh Table -->
            <b-button
              variant="outline-secondary"
              size="sm"
              class="btn-icon"
              @click="fetchDetails"
              v-b-tooltip.hover
              :title="$t('reloadSamples')"
            >
              <feather-icon icon="RotateCwIcon" size="14" :class="{ 'spin-icon': isLoadingDetails }" />
            </b-button>
          </div>
        </div>

        <!-- Table Component -->
        <b-table
          ref="refPayloadTable"
          class="position-relative mb-0 asset-data-table table-hover"
          tbody-tr-class="asset-table-row"
          :items="detailsData.data"
          responsive
          :fields="tableColumns"
          primary-key="dateTime"
          show-empty
          :busy="isLoadingDetails"
        >
          <!-- Table Busy Skeleton -->
          <template #table-busy>
            <div class="py-1 px-1">
              <div v-for="n in 5" :key="'skeleton-row-' + n" class="p-1 mb-50 rounded bg-light-secondary">
                <b-row class="align-items-center">
                  <b-col cols="2"><b-skeleton width="85%" height="18px" animation="wave" /></b-col>
                  <b-col cols="2"><b-skeleton width="70%" height="18px" animation="wave" /></b-col>
                  <b-col cols="2"><b-skeleton width="60%" height="18px" animation="wave" /></b-col>
                  <b-col cols="2"><b-skeleton width="75%" height="18px" animation="wave" /></b-col>
                  <b-col cols="2"><b-skeleton width="60%" height="18px" animation="wave" /></b-col>
                  <b-col cols="2"><b-skeleton width="80%" height="22px" animation="wave" /></b-col>
                </b-row>
              </div>
            </div>
          </template>

          <!-- Empty State Slot -->
          <template #empty>
            <div class="text-center py-4 px-2">
              <b-avatar variant="light-secondary" size="64px" class="mb-1">
                <feather-icon icon="InboxIcon" size="32" class="text-muted" />
              </b-avatar>
              <h5 class="font-weight-bolder mb-50">{{ $t('noTelemetrySampleFound') }}</h5>
              <p class="text-muted font-small-3 mb-1 mx-auto" style="max-width: 450px;">
                {{ $t('noSamplesWithLoadDataRegisteredForAppliedFilters') }}
              </p>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="clearFilters"
                class="d-inline-flex align-items-center"
              >
                <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />
                <span>{{ $t('resetFilters') }}</span>
              </b-button>
            </div>
          </template>

          <!-- Column: Date / Time -->
          <template #cell(dateTime)="data">
            <div class="d-inline-flex flex-column">
              <div class="d-flex align-items-center font-weight-bolder text-body font-small-2">
                <feather-icon icon="ClockIcon" size="12" class="mr-25 text-primary flex-shrink-0" />
                <span>{{ formatDateTimeDisplay(data.item.dateTime) }}</span>
              </div>
              <small class="text-muted font-small-1">UTC: {{ data.item.dateTime }}</small>
            </div>
          </template>

          <!-- Column: Carga Acumulada -->
          <template #cell(accumulatedLoad_tcg)="data">
            <div class="d-inline-flex align-items-center font-weight-bold">
              <feather-icon icon="PackageIcon" size="12" class="mr-25 text-muted" />
              <span>{{ formatNumber(data.item.accumulatedLoad_tcg) }} <small class="text-muted">t</small></span>
            </div>
          </template>

          <!-- Column: Ciclos Acumulados -->
          <template #cell(accumulatedCycles_ccc)="data">
            <div class="d-inline-flex align-items-center">
              <feather-icon icon="RepeatIcon" size="12" class="mr-25 text-muted" />
              <span>{{ formatInteger(data.item.accumulatedCycles_ccc) }}</span>
            </div>
          </template>

          <!-- Column: Delta Carga -->
          <template #cell(deltaLoad)="data">
            <b-badge
              :variant="data.item.deltaLoad > 0 ? 'light-primary' : 'light-secondary'"
              pill
              class="font-weight-bold"
            >
              +{{ formatNumber(data.item.deltaLoad) }} t
            </b-badge>
          </template>

          <!-- Column: Delta Ciclos -->
          <template #cell(deltaCycles)="data">
            <b-badge
              :variant="data.item.deltaCycles > 0 ? 'light-info' : 'light-secondary'"
              pill
              class="font-weight-bold"
            >
              +{{ formatInteger(data.item.deltaCycles) }}
            </b-badge>
          </template>

          <!-- Column: Carga Média do Ciclo -->
          <template #cell(avgLoad)="data">
            <span v-if="data.item.avgLoad !== null && data.item.avgLoad !== undefined" class="font-weight-bold">
              {{ formatNumber(data.item.avgLoad) }} <small class="text-muted">t/ciclo</small>
            </span>
            <span v-else class="text-muted font-small-2">—</span>
          </template>

          <!-- Column: Status da Carga -->
          <template #cell(loadStatus)="data">
            <b-badge
              v-if="data.item.loadStatus"
              :variant="resolveStatusVariant(data.item.loadStatus)"
              pill
              class="d-inline-flex align-items-center py-50 px-75 font-small-2 font-weight-bold text-nowrap"
            >
              <feather-icon :icon="resolveStatusIcon(data.item.loadStatus)" size="12" class="mr-25" />
              <span>{{ resolveStatusLabel(data.item.loadStatus) }}</span>
            </b-badge>
            <span v-else class="text-muted font-small-2">—</span>
          </template>
        </b-table>

        <!-- Table Footer: Pagination & Counts -->
        <div
          v-if="detailsData.totalItems > 0"
          class="asset-list-footer p-1 border-top d-flex align-items-center justify-content-between flex-wrap"
          style="gap: 0.75rem;"
        >
          <div class="d-flex align-items-center font-small-3 text-muted">
            <span>
              {{ $t('showing') }} <strong>{{ paginationFrom }}</strong> {{ $t('to') }}
              <strong>{{ paginationTo }}</strong> {{ $t('of') }}
              <strong>{{ detailsData.totalItems }}</strong> {{ $t('samples') }}
            </span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="detailsData.totalItems"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 pagination-modern"
            prev-class="prev-item"
            next-class="next-item"
            @input="onPageChange"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-card>
    </template>
  </section>
</template>

<script>
/* eslint-disable import/no-cycle, no-param-reassign */
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { resolveIcon } from '@/utils/assetUtils'
import defaultCarIcon from '@/assets/images/assets/Car.png'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BButtonGroup,
  BAlert,
  BTable,
  BBadge,
  BPagination,
  BAvatar,
  BSpinner,
  BSkeleton,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BFormInput,
  BLink,
  BPopover,
  VBToggle,
  VBTooltip,
  VBPopover,
} from 'bootstrap-vue'

import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import VueApexCharts from 'vue-apexcharts'
import payloadStoreModule from './payloadStoreModule'

/**
 * Formata data para envio ao backend (sem offset de timezone, ajustado no backend):
 * - Data inicial: primeira hora do dia (00:00:01)
 * - Data final: última hora do dia (23:59:59)
 */
function formatToBackendDate(date, isEndOfDay = false) {
  if (!date) return ''
  let ymd = ''
  if (typeof date === 'string') {
    const match = date.match(/^\d{4}-\d{2}-\d{2}/)
    if (match) {
      [ymd] = match
    } else {
      const d = new Date(date)
      if (!Number.isNaN(d.getTime())) {
        const pad = n => String(n).padStart(2, '0')
        ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      }
    }
  } else if (date instanceof Date && !Number.isNaN(date.getTime())) {
    const pad = n => String(n).padStart(2, '0')
    ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  if (!ymd) return ''
  return isEndOfDay ? `${ymd}T23:59:59` : `${ymd}T00:00:01`
}

export default {
  name: 'PayloadReport',
  directives: {
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
    'b-popover': VBPopover,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BButtonGroup,
    BAlert,
    BTable,
    BBadge,
    BPagination,
    BAvatar,
    BSpinner,
    BSkeleton,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
    BLink,
    BPopover,
    flatPickr,
    vSelect,
    VueApexCharts,
  },
  setup(props, { root }) {
    const PAYLOAD_APP_STORE_MODULE_NAME = 'app-payload'

    // Registrar storeModule dinamicamente
    if (!store.hasModule(PAYLOAD_APP_STORE_MODULE_NAME)) {
      store.registerModule(PAYLOAD_APP_STORE_MODULE_NAME, payloadStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(PAYLOAD_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(PAYLOAD_APP_STORE_MODULE_NAME)
      }
    })

    // UI state
    const showFilters = ref(false)
    const showTipBanner = ref(true)
    const targetLoadInput = ref(null)

    // Loadings
    const isLoadingSummary = ref(false)
    const isLoadingAggregations = ref(false)
    const isLoadingDetails = ref(false)

    // Asset Info & Data (URL Locked)
    const assetData = ref(null)
    const loadingAsset = ref(false)
    const assetId = ref('')

    const routeAssetId = computed(() => (
      (root.$route.params && root.$route.params.id)
      || (root.$route.query && root.$route.query.id)
      || ''
    ))

    const assetPlate = computed(() => assetData.value?.plate || '')
    const assetChassis = computed(() => assetData.value?.chassisNumber || '')
    const assetManufacturer = computed(() => (
      assetData.value?.manufacturerName
      || assetData.value?.brandName
      || assetData.value?.manufacturer
      || ''
    ))
    const assetModel = computed(() => {
      if (!assetData.value) return ''
      const name = assetData.value.modelIdName || assetData.value.modelName || ''
      const year = assetData.value.modelYear ? `(${assetData.value.modelYear})` : ''
      return `${name} ${year}`.trim()
    })
    const assetAvatar = computed(() => {
      if (!assetData.value) return resolveIcon(null, null)
      return resolveIcon(assetData.value.assetType, assetData.value.iconUrl)
    })
    const assetGroupName = computed(() => (
      assetData.value?.assetGroupName
      || assetData.value?.groupName
      || ''
    ))
    const assetCustomerName = computed(() => (
      assetData.value?.customerName
      || assetData.value?.ownerName
      || ''
    ))

    // Filter fields
    const dateStart = ref('')
    const dateEnd = ref('')
    const targetLoad = ref('')
    const dateRangePreset = ref('today')

    // Table & Pagination
    const currentPage = ref(1)
    const perPage = ref(50)
    const perPageOptions = [10, 25, 50, 100]

    // Aggregations GroupBy
    const groupBy = ref('Hour')

    // Data containers
    const summaryData = ref(null)
    const aggregationsData = ref(null)
    const detailsData = ref({
      data: [],
      length: 0,
      totalItems: 0,
    })

    // Table columns configuration
    const tableColumns = [
      { key: 'dateTime', label: root.$t('dateAndTime'), sortable: true },
      { key: 'accumulatedLoad_tcg', label: root.$t('accumLoadT'), sortable: true },
      { key: 'accumulatedCycles_ccc', label: root.$t('accumCycles'), sortable: true },
      { key: 'deltaLoad', label: root.$t('deltaLoadTParens'), sortable: true },
      { key: 'deltaCycles', label: root.$t('deltaCyclesTitle'), sortable: true },
      { key: 'avgLoad', label: root.$t('averageCycleT'), sortable: true },
      { key: 'loadStatus', label: root.$t('status'), sortable: true },
    ]

    // FlatPickr configuration
    const flatPickrConfig = computed(() => ({
      locale: getFlatpickrLocale(),
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'd/m/Y',
    }))

    // Active filters count (filtros configuráveis)
    const activeFiltersCount = computed(() => {
      let count = 0
      if (dateStart.value) count += 1
      if (dateEnd.value) count += 1
      if (targetLoad.value) count += 1
      return count
    })

    const hasTargetLoad = computed(() => (
      targetLoad.value !== null
      && targetLoad.value !== undefined
      && targetLoad.value !== ''
      && Number(targetLoad.value) > 0
    ))

    // Computed Summary KPIs
    const summaryKpis = computed(() => {
      if (summaryData.value && summaryData.value.kpis) {
        return summaryData.value.kpis
      }
      return {
        totalLoad: 0,
        totalCycles: 0,
        avgLoadPerCycle: null,
        complianceRate: null,
      }
    })

    // Computed Load Distribution
    const summaryDistribution = computed(() => (
      summaryData.value && summaryData.value.loadDistribution ? summaryData.value.loadDistribution : null
    ))

    // Computed Time Series
    const timeSeriesData = computed(() => (
      aggregationsData.value && aggregationsData.value.timeSeries ? aggregationsData.value.timeSeries : []
    ))

    // Largura mínima dinâmica para rolagem horizontal suave quando houver muitos pontos
    const timeSeriesChartMinWidth = computed(() => {
      const pointsCount = (timeSeriesData.value && timeSeriesData.value.length) || 0
      if (pointsCount <= 16) return '100%'
      return `${Math.max(800, pointsCount * 45)}px`
    })

    // Computed Histogram
    const histogramData = computed(() => (
      aggregationsData.value && aggregationsData.value.histogram ? aggregationsData.value.histogram : []
    ))

    // Pagination calculations
    const paginationFrom = computed(() => {
      if (detailsData.value.totalItems === 0) return 0
      return (currentPage.value - 1) * perPage.value + 1
    })

    const paginationTo = computed(() => {
      const to = currentPage.value * perPage.value
      return to > detailsData.value.totalItems ? detailsData.value.totalItems : to
    })

    // Formatting helpers
    const formatNumber = (val, suffix = '') => {
      if (val === null || val === undefined || Number.isNaN(val)) return '—'
      const formatted = Number(val).toLocaleString(getIntlLocale(), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      })
      return suffix ? `${formatted} ${suffix}` : formatted
    }

    const formatInteger = val => {
      if (val === null || val === undefined || Number.isNaN(val)) return '0'
      return Number(val).toLocaleString(getIntlLocale())
    }

    const formatDateDisplay = dateStr => {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    }

    const formatDateTimeDisplay = isoStr => {
      if (!isoStr) return '—'
      const d = new Date(isoStr)
      if (Number.isNaN(d.getTime())) return isoStr
      return d.toLocaleString(getIntlLocale(), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    }

    const resolveStatusVariant = status => {
      if (!status) return 'light-secondary'
      const s = String(status).toLowerCase()
      if (s.includes('under') || s === 'subcarga') return 'light-warning'
      if (s.includes('optimal') || s === 'ideal' || s === 'conforme') return 'light-success'
      if (s.includes('severe') || s === 'sobrecarga severa') return 'severe-overload'
      if (s.includes('over') || s === 'sobrecarga') return 'light-danger'
      return 'light-secondary'
    }

    const resolveStatusIcon = status => {
      if (!status) return 'InfoIcon'
      const s = String(status).toLowerCase()
      if (s.includes('under') || s === 'subcarga') return 'ArrowDownIcon'
      if (s.includes('optimal') || s === 'ideal' || s === 'conforme') return 'CheckCircleIcon'
      if (s.includes('severe') || s === 'sobrecarga severa') return 'AlertOctagonIcon'
      if (s.includes('over') || s === 'sobrecarga') return 'AlertTriangleIcon'
      return 'InfoIcon'
    }

    const resolveStatusLabel = status => {
      if (!status) return '—'
      const s = String(status).toLowerCase()
      if (s.includes('under')) return root.$t('underloadLt90')
      if (s.includes('optimal')) return root.$t('optimal90To110')
      if (s.includes('severe')) return root.$t('severeOverloadGt120')
      if (s.includes('over')) return root.$t('overload110To120')
      return status
    }

    const getComplianceVariantClass = rate => {
      if (rate >= 80) return 'text-success'
      if (rate >= 50) return 'text-warning'
      return 'text-danger'
    }

    // Charts Options & Series
    // 1. Donut Chart (Load Distribution / Cycles)
    const donutChartSeries = computed(() => {
      if (!summaryDistribution.value) return [0, 0, 0, 0]
      const u = summaryDistribution.value.underload ? summaryDistribution.value.underload.cycles : 0
      const o = summaryDistribution.value.optimal ? summaryDistribution.value.optimal.cycles : 0
      const ov = summaryDistribution.value.overload ? summaryDistribution.value.overload.cycles : 0
      const so = summaryDistribution.value.severeOverload ? summaryDistribution.value.severeOverload.cycles : 0
      return [u, o, ov, so]
    })

    const donutChartOptions = computed(() => ({
      chart: {
        type: 'donut',
        toolbar: { show: false },
      },
      labels: [root.$t('underloadLt90'), root.$t('optimal90To110'), root.$t('overload110To120'), root.$t('severeOverloadGt120')],
      colors: ['#ff9f43', '#28c76f', '#ea5455', '#9b111e'],
      dataLabels: {
        enabled: true,
        formatter(val) {
          return `${val.toFixed(1)}%`
        },
        style: {
          fontSize: '13px',
          fontWeight: '600',
        },
        dropShadow: { enabled: false },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '13px',
        itemMargin: { horizontal: 12, vertical: 6 },
        labels: {
          colors: '#6e6b7b',
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                fontWeight: 600,
                color: '#5e5873',
                offsetY: -6,
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 700,
                color: '#5e5873',
                offsetY: 6,
                formatter(val) {
                  return Number(val).toLocaleString(getIntlLocale())
                },
              },
              total: {
                show: true,
                label: root.$t('totalCycles'),
                fontSize: '13px',
                fontWeight: 600,
                color: '#6e6b7b',
                formatter: () => {
                  if (!summaryDistribution.value) return '0'
                  const u = summaryDistribution.value.underload ? summaryDistribution.value.underload.cycles : 0
                  const o = summaryDistribution.value.optimal ? summaryDistribution.value.optimal.cycles : 0
                  const ov = summaryDistribution.value.overload ? summaryDistribution.value.overload.cycles : 0
                  const so = summaryDistribution.value.severeOverload ? summaryDistribution.value.severeOverload.cycles : 0
                  return Number(u + o + ov + so).toLocaleString(getIntlLocale())
                },
              },
            },
          },
        },
      },
    }))

    // 2. Histogram Chart (Columns)
    const histogramChartSeries = computed(() => {
      if (!histogramData.value || histogramData.value.length === 0) return []
      return [
        {
          name: root.$t('cyclesTitle'),
          data: histogramData.value.map(h => h.cycles),
        },
      ]
    })

    const histogramChartOptions = computed(() => {
      const categories = histogramData.value.map(h => h.range)
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            distributed: true,
            borderRadius: 6,
            columnWidth: '40%',
          },
        },
        colors: ['#ff9f43', '#28c76f', '#ea5455', '#7367f0', '#00cfe8'],
        grid: {
          padding: {
            bottom: 15,
          },
        },
        xaxis: {
          categories,
          title: {
            text: root.$t('loadWeightRangeTons'),
            offsetY: 10,
            style: {
              color: '#6e6b7b',
              fontSize: '12px',
              fontWeight: 600,
            },
          },
          labels: {
            style: { colors: '#6e6b7b', fontSize: '11px', fontWeight: '500' },
          },
        },
        yaxis: {
          title: {
            text: root.$t('amountOfCyclesCompleted'),
            style: {
              color: '#6e6b7b',
              fontSize: '12px',
              fontWeight: 600,
            },
          },
          labels: {
            formatter(val) {
              return Math.round(val)
            },
            style: { colors: '#6e6b7b', fontSize: '11px' },
          },
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter(val) {
            return `${val} cic`
          },
          style: {
            fontSize: '11px',
            fontWeight: '600',
          },
        },
        tooltip: {
          y: {
            formatter(val) {
              return `${val} ${root.$t('cyclesCompletedLowerCase')}`
            },
          },
        },
      }
    })

    // 3. Time Series Area Chart
    const timeSeriesChartSeries = computed(() => {
      if (!timeSeriesData.value || timeSeriesData.value.length === 0) return []
      return [
        {
          name: root.$t('averagePerCycleTParens'),
          data: timeSeriesData.value.map(item => ({
            x: item.timestamp,
            y: item.avgLoadPerCycle !== null && item.avgLoadPerCycle !== undefined
              ? Number(Number(item.avgLoadPerCycle).toFixed(2))
              : 0,
          })),
        }
      ]
    })

    const timeSeriesYAxisScale = computed(() => {
      if (!timeSeriesData.value || timeSeriesData.value.length === 0) {
        return { min: 0, max: undefined, title: root.$t('averagePerCycleTParens') }
      }

      const values = timeSeriesData.value.map(item => item.avgLoadPerCycle || 0)
      const minValue = Math.min(...values)
      const maxValue = Math.max(...values)
      const range = maxValue - minValue
      const padding = range === 0 ? Math.max(Math.abs(maxValue) * 0.15, 1) : range * 0.12

      return {
        min: Math.max(0, minValue - padding),
        max: maxValue + padding,
        title: root.$t('averagePerCycleTParens'),
      }
    })

    const timeSeriesChartOptions = computed(() => {
      const yScale = timeSeriesYAxisScale.value

      return {
        chart: {
          type: 'area',
          height: 350,
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 450,
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
        },
        grid: {
          padding: {
            top: -10,
            bottom: 25,
            left: 15,
            right: 15,
          },
        },
        colors: ['#28c76f'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        markers: {
          size: 5,
          strokeColors: '#ffffff',
          strokeWidth: 2,
          strokeOpacity: 1,
          fillOpacity: 1,
          hover: {
            size: 8,
            sizeOffset: 3,
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.5,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          type: 'datetime',
          title: {
            text: groupBy.value === 'Hour'
              ? root.$t('timePeriodHoursMinutes')
              : root.$t('timePeriodDays'),
            offsetY: 20,
            style: {
              color: '#6e6b7b',
              fontSize: '12px',
              fontWeight: 600,
            },
          },
          labels: {
            datetimeUTC: false,
            offsetY: 0,
            style: { colors: '#6e6b7b', fontSize: '11px' },
          },
        },
        yaxis: {
          min: yScale.min,
          max: yScale.max,
          forceNiceScale: true,
          title: {
            text: yScale.title,
            style: {
              color: '#6e6b7b',
              fontSize: '12px',
              fontWeight: 600,
            },
          },
          labels: {
            formatter(val) {
              return Number(val).toFixed(1)
            },
            style: { colors: '#6e6b7b', fontSize: '11px' },
          },
        },
        tooltip: {
          custom({ series, seriesIndex, dataPointIndex, w }) {
            const item = timeSeriesData.value[dataPointIndex]
            if (!item) return ''
            
            let dateStr = ''
            if (groupBy.value === 'Hour') {
               dateStr = formatDateTimeDisplay(item.timestamp)
            } else {
               const datePart = item.timestamp && typeof item.timestamp === 'string' ? item.timestamp.split('T')[0] : item.timestamp
               dateStr = formatDateDisplay(datePart)
            }
            
            const avgLoad = formatNumber(item.avgLoadPerCycle)
            const deltaLoad = formatNumber(item.deltaLoad)
            const deltaCycles = formatInteger(item.deltaCycles)

            return `
              <div class="px-1 py-75 shadow rounded bg-white" style="border: 1px solid rgba(34, 41, 47, 0.1); min-width: 200px; font-family: inherit;">
                <div class="text-muted font-small-2 mb-50 pb-25">
                  <feather-icon icon="CalendarIcon" size="12" class="mr-25"></feather-icon>
                  ${dateStr}
                </div>
                
                <div class="d-flex justify-content-between align-items-center mb-50">
                  <span class="font-weight-bold text-dark d-flex align-items-center font-small-3">
                    <span class="bullet bullet-sm bullet-success mr-50" style="background-color: #28c76f;"></span>
                    ${root.$t('averagePerCycle')}
                  </span>
                  <span class="font-weight-bolder text-success ml-1" style="font-size: 15px;">
                    ${avgLoad} <small class="text-muted" style="font-size: 11px;">t</small>
                  </span>
                </div>
                
                <div class="pt-50 mt-50 border-top">
                  <div class="d-flex flex-column font-small-1 text-muted" style="gap: 4px;">
                    <div class="d-flex justify-content-between align-items-center">
                      <span>${root.$t('deltaLoad')}</span>
                      <span class="font-weight-bold">${deltaLoad} t</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span>${root.$t('deltaCyclesTitle')}</span>
                      <span class="font-weight-bold">${deltaCycles}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
          }
        },
        legend: {
          show: false,
        },
      }
    })

    // Handlers & Methods
    const showToastError = err => {
      const message = (err.response && err.response.data && (err.response.data.message || err.response.data.title))
        || err.message
        || root.$t('errorQueryingTelemetryData')
      root.$bvToast.toast(message, {
        title: `❌ ${root.$t('error')}`,
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-bottom-right',
      })
    }

    const setDatePreset = preset => {
      dateRangePreset.value = preset
      const today = new Date()
      const pad = n => String(n).padStart(2, '0')
      const formatYMD = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

      if (preset === 'today') {
        dateStart.value = formatYMD(today)
        dateEnd.value = formatYMD(today)
      } else if (preset === 'yesterday') {
        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        dateStart.value = formatYMD(yesterday)
        dateEnd.value = formatYMD(yesterday)
      } else if (preset === 'last7') {
        const start = new Date(today)
        start.setDate(today.getDate() - 6)
        dateStart.value = formatYMD(start)
        dateEnd.value = formatYMD(today)
      } else if (preset === 'month') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1)
        dateStart.value = formatYMD(start)
        dateEnd.value = formatYMD(today)
      }
    }

    const onCustomDateChange = () => {
      dateRangePreset.value = 'custom'
    }

    // API calls
    const buildCommonParams = () => ({
      assetId: assetId.value,
      startDate: formatToBackendDate(dateStart.value, false),
      endDate: formatToBackendDate(dateEnd.value, true),
      targetLoad: hasTargetLoad.value ? Number(targetLoad.value) : undefined,
    })

    const fetchSummary = () => {
      if (!assetId.value) return
      isLoadingSummary.value = true
      const params = buildCommonParams()

      store
        .dispatch(`${PAYLOAD_APP_STORE_MODULE_NAME}/fetchPayloadSummary`, params)
        .then(response => {
          summaryData.value = response.data
        })
        .catch(err => {
          summaryData.value = null
          showToastError(err)
        })
        .finally(() => {
          isLoadingSummary.value = false
        })
    }

    const fetchAggregations = () => {
      if (!assetId.value) return
      isLoadingAggregations.value = true
      const params = {
        ...buildCommonParams(),
        groupBy: groupBy.value,
      }

      store
        .dispatch(`${PAYLOAD_APP_STORE_MODULE_NAME}/fetchPayloadAggregations`, params)
        .then(response => {
          aggregationsData.value = response.data
        })
        .catch(err => {
          aggregationsData.value = null
          showToastError(err)
        })
        .finally(() => {
          isLoadingAggregations.value = false
        })
    }

    const fetchDetails = () => {
      if (!assetId.value) return
      isLoadingDetails.value = true
      const params = {
        ...buildCommonParams(),
        page: currentPage.value,
        perPage: perPage.value,
      }

      store
        .dispatch(`${PAYLOAD_APP_STORE_MODULE_NAME}/fetchPayloadDetails`, params)
        .then(response => {
          if (response && response.data) {
            detailsData.value = {
              data: response.data.data || [],
              length: response.data.length || 0,
              totalItems: response.data.totalItems || 0,
            }
          }
        })
        .catch(err => {
          detailsData.value = { data: [], length: 0, totalItems: 0 }
          showToastError(err)
        })
        .finally(() => {
          isLoadingDetails.value = false
        })
    }

    const fetchData = () => {
      if (!assetId.value) {
        root.$bvToast.toast(root.$t('pleaseSelectAssetToGenerateReport'), {
          title: `⚠️ ${root.$t('attention')}`,
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        })
        showFilters.value = true
        return
      }

      if (!dateStart.value || !dateEnd.value) {
        root.$bvToast.toast(root.$t('pleaseDefineStartAndEndDate'), {
          title: `⚠️ ${root.$t('attention')}`,
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        })
        return
      }

      currentPage.value = 1
      fetchSummary()
      fetchAggregations()
      fetchDetails()
    }

    const clearFilters = () => {
      targetLoad.value = ''
      perPage.value = 50
      currentPage.value = 1
      setDatePreset('today')
      fetchData()
    }

    const clearTargetLoad = () => {
      targetLoad.value = ''
      fetchData()
    }

    const openFilterForTargetLoad = () => {
      showFilters.value = true
      root.$nextTick(() => {
        if (targetLoadInput.value && targetLoadInput.value.$el) {
          targetLoadInput.value.$el.focus()
        }
      })
    }

    const goToAssetSummary = () => {
      if (assetId.value) {
        root.$router.push({ name: 'apps-asset-summary', params: { id: assetId.value } })
      }
    }

    const goBack = () => {
      root.$router.back()
    }

    const handleImageError = event => {
      event.target.src = defaultCarIcon
    }

    const copyToClipboard = (text, label) => {
      if (!text) return
      const successTitle = `${label || root.$t('text')} ${root.$t('copiedToClipboard')}!`
      const failTitle = root.$t('failedToCopyToClipboard')

      const notifySuccess = () => {
        root.$toast({
          component: ToastificationContent,
          props: {
            title: successTitle,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      }

      const notifyError = () => {
        root.$toast({
          component: ToastificationContent,
          props: {
            title: failTitle,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }

      if (root.$copyText) {
        root.$copyText(text).then(notifySuccess).catch(notifyError)
      } else if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(notifySuccess).catch(notifyError)
      }
    }

    // Carregar informações do ativo pelo ID
    const loadAssetData = id => {
      if (!id) return Promise.resolve(null)
      loadingAsset.value = true
      return store
        .dispatch(`${PAYLOAD_APP_STORE_MODULE_NAME}/fetchAsset`, { id })
        .then(response => {
          if (response && response.data) {
            assetData.value = response.data
          }
          return response
        })
        .catch(() => {
          // Mantém assetId para consulta mesmo se os metadados detalhados falharem
        })
        .finally(() => {
          loadingAsset.value = false
        })
    }

    const onSearchClick = () => {
      fetchData()
    }

    const changeGroupBy = newGroup => {
      if (groupBy.value === newGroup) return
      groupBy.value = newGroup
      fetchAggregations()
    }

    const onPageChange = val => {
      currentPage.value = val
      fetchDetails()
    }

    const onPerPageChange = val => {
      perPage.value = val
      currentPage.value = 1
      fetchDetails()
    }

    // CSV Export
    const exportToCsv = () => {
      if (!detailsData.value.data || detailsData.value.data.length === 0) return
      const headers = [
        'Data / Hora',
        'Carga Acumulada (t)',
        'Ciclos Acumulados',
        'Delta Carga (t)',
        'Delta Ciclos',
        'Carga Media (t)',
        'Status',
      ]
      const rows = detailsData.value.data.map(item => [
        `"${formatDateTimeDisplay(item.dateTime)}"`,
        item.accumulatedLoad_tcg,
        item.accumulatedCycles_ccc,
        item.deltaLoad,
        item.deltaCycles,
        item.avgLoad !== null ? item.avgLoad : '',
        `"${item.loadStatus || ''}"`,
      ])

      const csvContent = `data:text/csv;charset=utf-8,\uFEFF${[headers.join(';'), ...rows.map(e => e.join(';'))].join('\n')}`
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `payload_report_${assetPlate.value || assetChassis.value || assetId.value || 'asset'}_${dateStart.value}_${dateEnd.value}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    // Lifecycle
    onMounted(() => {
      setDatePreset('today')
      const id = routeAssetId.value
      if (id) {
        assetId.value = id
        loadAssetData(id).then(() => {
          fetchData()
        })
      }
    })

    watch(
      () => routeAssetId.value,
      newId => {
        if (newId && newId !== assetId.value) {
          assetId.value = newId
          loadAssetData(newId).then(() => {
            fetchData()
          })
        }
      },
    )

    return {
      showFilters,
      showTipBanner,
      targetLoadInput,
      isLoadingSummary,
      isLoadingAggregations,
      isLoadingDetails,
      assetId,
      assetData,
      loadingAsset,
      assetPlate,
      assetChassis,
      assetManufacturer,
      assetModel,
      assetAvatar,
      assetGroupName,
      assetCustomerName,
      dateStart,
      dateEnd,
      targetLoad,
      dateRangePreset,
      currentPage,
      perPage,
      perPageOptions,
      groupBy,
      summaryData,
      summaryKpis,
      summaryDistribution,
      aggregationsData,
      timeSeriesData,
      histogramData,
      detailsData,
      tableColumns,
      flatPickrConfig,
      activeFiltersCount,
      hasTargetLoad,
      paginationFrom,
      paginationTo,
      donutChartSeries,
      donutChartOptions,
      histogramChartSeries,
      histogramChartOptions,
      timeSeriesChartSeries,
      timeSeriesChartOptions,
      timeSeriesChartMinWidth,
      setDatePreset,
      onCustomDateChange,
      clearFilters,
      clearTargetLoad,
      openFilterForTargetLoad,
      goToAssetSummary,
      goBack,
      handleImageError,
      copyToClipboard,
      fetchData,
      fetchDetails,
      onSearchClick,
      changeGroupBy,
      onPageChange,
      onPerPageChange,
      formatNumber,
      formatInteger,
      formatDateDisplay,
      formatDateTimeDisplay,
      resolveStatusVariant,
      resolveStatusIcon,
      resolveStatusLabel,
      getComplianceVariantClass,
      exportToCsv,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.payload-report-wrapper {
  .filters-card {
    border-radius: 0.5rem;
  }
}

// Per Page Selector
.per-page-selector {
  width: 84px;
}

// Spin Icon Animation
.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

// KPI Cards
.kpi-card {
  border-radius: 0.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px 0 rgba(34, 41, 47, 0.1) !important;
  }

  .kpi-label {
    letter-spacing: 0.05em;
  }

  .kpi-unit {
    font-size: 0.9rem;
    font-weight: 500;
    color: #6e6b7b;
  }
}

// Distribution Box
.distribution-pill-box {
  transition: transform 0.15s ease;

  &:hover {
    transform: translateX(3px);
  }
}

// Custom Severe Overload Styles
.text-severe {
  color: #9b111e !important;
}
.severe-overload {
  background-color: rgba(155, 17, 30, 0.1) !important;
  border-color: #9b111e !important;
}
.badge-severe-overload {
  background-color: rgba(155, 17, 30, 0.12) !important;
  color: #9b111e !important;
}

// Promo card when target load is missing
.target-load-promo-card {
  background: linear-gradient(118deg, rgba(255, 159, 67, 0.08), rgba(255, 159, 67, 0.02));
  border: 1px dashed rgba(255, 159, 67, 0.4) !important;
}

// Bullet Dot
.bullet-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

// Time Series Scroll Container
.time-series-scroll-container {
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;

  &::-webkit-scrollbar {
    height: 7px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d2d6;
    border-radius: 4px;

    &:hover {
      background: #b4b7bd;
    }
  }
}

// Series visibility chips on line chart
.series-selector {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(115, 103, 240, 0.04);
  border: 1px solid rgba(115, 103, 240, 0.12);
}

.series-selector-header {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6e6b7b;
  line-height: 1;
}

.series-selector-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.series-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 5px 10px 5px 6px;
  border-radius: 999px;
  border: 1.5px solid #d8d6de;
  background: #ffffff;
  color: #6e6b7b;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: none;
  opacity: 0.7;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.18);
  }

  .series-chip-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ebe9f1;
    color: #b9b9c3;
    flex-shrink: 0;
    transition: all 0.18s ease;
  }

  .series-chip-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #c7c5d1;
    flex-shrink: 0;
    transition: all 0.18s ease;
  }

  .series-chip-label {
    white-space: nowrap;
  }

  .series-chip-status {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #b9b9c3;
    padding: 2px 6px;
    border-radius: 999px;
    background: #f3f2f7;
    line-height: 1.3;
  }

  &.is-active {
    opacity: 1;
    color: #5e5873;
  }

  // Delta Carga (primary purple)
  &.series-chip--deltaLoad {
    &:hover {
      border-color: #7367f0;
    }

    &.is-active {
      border-color: #7367f0;
      background: rgba(115, 103, 240, 0.12);
      box-shadow: 0 2px 8px rgba(115, 103, 240, 0.22);

      .series-chip-check {
        background: #7367f0;
        color: #ffffff;
      }

      .series-chip-dot {
        background: #7367f0;
        box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.25);
      }

      .series-chip-status {
        color: #7367f0;
        background: rgba(115, 103, 240, 0.16);
      }
    }
  }

  // Delta Ciclos (info cyan)
  &.series-chip--deltaCycles {
    &:hover {
      border-color: #00cfe8;
    }

    &.is-active {
      border-color: #00cfe8;
      background: rgba(0, 207, 232, 0.12);
      box-shadow: 0 2px 8px rgba(0, 207, 232, 0.22);

      .series-chip-check {
        background: #00cfe8;
        color: #ffffff;
      }

      .series-chip-dot {
        background: #00cfe8;
        box-shadow: 0 0 0 3px rgba(0, 207, 232, 0.25);
      }

      .series-chip-status {
        color: #00b8d4;
        background: rgba(0, 207, 232, 0.16);
      }
    }
  }

  // Média por Ciclo (success green)
  &.series-chip--avgLoadPerCycle {
    &:hover {
      border-color: #28c76f;
    }

    &.is-active {
      border-color: #28c76f;
      background: rgba(40, 199, 111, 0.12);
      box-shadow: 0 2px 8px rgba(40, 199, 111, 0.22);

      .series-chip-check {
        background: #28c76f;
        color: #ffffff;
      }

      .series-chip-dot {
        background: #28c76f;
        box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.25);
      }

      .series-chip-status {
        color: #28c76f;
        background: rgba(40, 199, 111, 0.16);
      }
    }
  }
}

// Deltas Popover
.deltas-popover {
  max-width: 450px;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.15) !important;
}

// Data Table Styling
.asset-data-table {
  ::v-deep tbody tr.asset-table-row {
    transition: background-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      background-color: rgba(115, 103, 240, 0.04) !important;
      box-shadow: inset 3px 0 0 #7367f0;
    }
  }

  ::v-deep tr.b-table-busy-slot {
    > td {
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
    }
  }
}

// Slide Fade Transition
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

// Compact Asset Info Header Card
.asset-header-card {
  border: 1px solid rgba(115, 103, 240, 0.14) !important;
  border-left: 4px solid #7367f0 !important;
  border-radius: 0.5rem;
  background-color: #ffffff;
  transition: all 0.2s ease-in-out;

  .asset-header-content {
    min-height: 60px;
    display: flex;
    align-items: center;
  }

  .back-btn {
    border: 1px solid rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
    &:hover {
      background-color: rgba(115, 103, 240, 0.1) !important;
      color: #7367f0 !important;
      border-color: #7367f0;
    }
  }

  .asset-avatar-box {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

    .asset-avatar-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 4px;
      transition: transform 0.2s ease;
    }

    &:hover .asset-avatar-img {
      transform: scale(1.08);
    }
  }

  .license-plate-badge {
    display: inline-flex;
    align-items: center;
    background: #ffffff;
    border: 2px solid #2b2b2b;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 0.95rem;
    font-weight: 800;
    letter-spacing: 1px;
    color: #2b2b2b;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.15s ease;

    .plate-number {
      font-family: 'Consolas', 'Courier New', monospace;
    }

    .copy-icon {
      opacity: 0.6;
      transition: opacity 0.15s ease;
    }

    &:hover {
      border-color: #7367f0;
      color: #7367f0;
      background-color: rgba(115, 103, 240, 0.05);

      .copy-icon {
        opacity: 1;
        color: #7367f0 !important;
      }
    }
  }

  .asset-brand-text {
    font-size: 1.05rem;
    letter-spacing: -0.2px;
    color: #7367f0 !important;
  }

  .asset-model-title {
    font-size: 1.05rem;
    letter-spacing: -0.2px;
  }

  .meta-chip {
    display: inline-flex;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #ebedf0;
    border-radius: 20px;
    padding: 3px 10px;
    font-size: 0.8rem;
    transition: all 0.15s ease;
    cursor: default;

    &.cursor-pointer, &[class*="cursor-pointer"] {
      cursor: pointer;
      &:hover {
        background: #f0f2f5;
        border-color: #7367f0;
      }
    }

    .meta-label {
      font-weight: 600;
      color: #6e6b7b;
      margin-right: 4px;
    }

    .meta-value {
      font-weight: 500;
      color: #5e5873;
    }

    .copy-icon {
      opacity: 0.6;
      transition: opacity 0.15s ease;
    }

    &:hover .copy-icon {
      opacity: 1;
      color: #7367f0 !important;
    }
  }

  .btn-view-details {
    font-weight: 600;
    border-radius: 6px;
    padding: 0.45rem 0.85rem;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;
  }
}

// Dark Layout Support
body.dark-layout,
.dark-layout {
  .asset-header-card {
    background-color: #283046 !important;
    border-color: rgba(255, 255, 255, 0.08) !important;
    border-left-color: #7367f0 !important;

    .back-btn {
      border-color: rgba(255, 255, 255, 0.12);
      color: #b4b7bd;
    }

    .asset-avatar-box {
      background: linear-gradient(135deg, #1e2433 0%, #283046 100%);
      border-color: rgba(255, 255, 255, 0.1);
    }

    .license-plate-badge {
      background: #161d31;
      border-color: #4b4b5a;
      color: #d0d2d6;

      &:hover {
        border-color: #7367f0;
        color: #7367f0;
        background-color: rgba(115, 103, 240, 0.1);
      }
    }

    .asset-brand-text {
      color: #9086f4 !important;
    }

    .asset-model-title {
      color: #d0d2d6 !important;
    }

    .meta-chip {
      background: #161d31;
      border-color: rgba(255, 255, 255, 0.08);

      &.cursor-pointer:hover {
        background: #1e2433;
        border-color: #7367f0;
      }

      .meta-label {
        color: #b4b7bd;
      }

      .meta-value {
        color: #d0d2d6;
      }
    }
  }

  .kpi-card {
    background: #283046;

    .kpi-unit {
      color: #b4b7bd;
    }
  }

  .distribution-pill-box {
    background: rgba(255, 255, 255, 0.03);
  }

  // Dark Layout Severe Overload
  .text-severe {
    color: #ff6b6b !important;
  }
  .severe-overload,
  .badge-severe-overload {
    background-color: rgba(255, 107, 107, 0.12) !important;
    border-color: #ff6b6b !important;
    color: #ff6b6b !important;
  }

  .target-load-promo-card {
    background: rgba(255, 159, 67, 0.05);
    border-color: rgba(255, 159, 67, 0.3) !important;
  }

  .time-series-scroll-container {
    &::-webkit-scrollbar-track {
      background: #161d31;
    }

    &::-webkit-scrollbar-thumb {
      background: #3b4253;

      &:hover {
        background: #4b5468;
      }
    }
  }

  .series-selector {
    background: rgba(115, 103, 240, 0.08);
    border-color: rgba(115, 103, 240, 0.2);
  }

  .series-selector-header {
    color: #b4b7bd;
  }

  .series-chip {
    background: #283046;
    border-color: #3b4253;
    color: #b4b7bd;

    .series-chip-check {
      background: #3b4253;
      color: #82868b;
    }

    .series-chip-status {
      background: #161d31;
      color: #82868b;
    }

    &.is-active {
      color: #d0d2d6;
    }

    &.series-chip--deltaLoad.is-active {
      background: rgba(115, 103, 240, 0.2);
    }

    &.series-chip--deltaCycles.is-active {
      background: rgba(0, 207, 232, 0.18);
    }

    &.series-chip--avgLoadPerCycle.is-active {
      background: rgba(40, 199, 111, 0.18);
    }
  }
}
</style>
